import React from 'react'
import styled from 'styled-components'
import FormPanel from '@src/components/FormPanel/FormPanel'
import FormPAETable, { PAETableRowType } from '@src/components/Form/PAETable'
import useModal from '@src/components/FormModal/useModal'
import { useSnackbar } from '@src/components/GlobalSnackbar'
import { useParams } from 'react-router'
import { DRSPageType, DRSSectionName, mapDRSPageTypeToPAE } from '@src/modules/DRS/DRSPage'
import { useConfirm } from '@src/components/GlobalConfirmDialog'
import { useDRS, transformSectionNameToDataSectionName } from '@src/modules/DRS/useDRS'
import Modal from '@src/components/FormModal/Modal'
import _get from 'lodash/get'
import { doubleUp } from '@src/modules/DRS/utils/doubleUp'
import { PAE } from '@src/types/drs/PAE'
import CrewSummaryFields, { CrewSummaryRowDisplay } from './CrewSummaryFields'
import { LostTimeFields } from './LostTimeFields'
import { mapMoreActions } from '@src/modules/DRS/utils/mapMoreActions'

interface CrewSummaryRow extends CrewSummaryRowDisplay {
  _key_: string,
  _type_: PAETableRowType,
  _no_?: number,
  _editable_: boolean,
}

const NULL_VALUES = {
  noWorkAvailable: null,
  wetWeatherHours: null,
  breakdownOfPlantOrEquipment: null,
  cancelledByCustomer: null,
  cancelledByInternalIssue: null,
  cancelledByCrewLeave: null,
  trainingCrew: null,
}
const ZERO_VALUES = {
  noWorkAvailable: '0',
  wetWeatherHours: '0',
  breakdownOfPlantOrEquipment: '0',
  cancelledByCustomer: '0',
  cancelledByInternalIssue: '0',
  cancelledByCrewLeave: '0',
  trainingCrew: '0',
}

const CompDRSLostTime = () => {
  const { id } = useParams<{ id: string }>()
  const { drs, updateViaPath, updateSectionViaPath, drsPageType, fetchDRS, refresh } = useDRS()
  const isPlan = drsPageType === DRSPageType.Plan
  const isEdit = drsPageType === DRSPageType.Edit
  const updatingColumn: PAE.Plan | PAE.Edit = isPlan ? PAE.Plan : PAE.Edit
  const { alertError, alertDone } = useSnackbar()
  const { confirm } = useConfirm()
  const [open, modalData, openAndSetData, closeModal] = useModal<CrewSummaryRow>({})
  if (drs === null) {
    return null
  }
  async function update(data: any) {
    try {
      await updateSectionViaPath({
        sectionName: DRSSectionName.Labour,
        path: `summary`,
        data,
      })
      refresh()
      closeModal()
      alertDone()
    } catch (e) {
      alertError()
    }
  }
  const handleSave = async (form: CrewSummaryRow) => {
    const retainedSummary = drs.labour.summary
    const data = {
      ...retainedSummary,
      [updatingColumn]: {
        ...retainedSummary[updatingColumn],
        ...form,
      }
    }
    update(data)
  }
  const handleRevert = async (form: CrewSummaryRow) => {
    const retainedSummary = drs.labour.summary
    const data = {
      ...retainedSummary,
      [updatingColumn]: {
        ...retainedSummary[updatingColumn],
        ...NULL_VALUES,
      }
    }
    update(data)
  }
  const handleMarkZero = async (form: CrewSummaryRow) => {
    const retainedSummary = drs.labour.summary
    const data = {
      ...retainedSummary,
      [updatingColumn]: {
        ...retainedSummary[updatingColumn],
        ...ZERO_VALUES,
      }
    }
    update(data)
  }
  const getTableData = () => {
    const rows = [drs.labour.summary]
    if (!rows || !rows.length) {
      return []
    }
    const doubled = doubleUp(rows, drsPageType)
    const doubledWithActions = doubled.map((data: any) => mapMoreActions(data, drsPageType))
    return doubledWithActions
  }
  return <Wrapper>
    <FormPanel title={`Lost Time`} subtitle={isEdit ? `Note: Record hours in shift only eg. 8` : ''} >
      <FormPAETable<CrewSummaryRow>
        fields={LostTimeFields}
        data={getTableData()}
        editable={true}
        isSelectable={true}
        onEditRow={(row: CrewSummaryRow) => {
          openAndSetData(row)
        }}
        showMoreActions={isEdit}
        onRevert={handleRevert}
        onMarkZero={handleMarkZero}
      />
    </FormPanel>

    {open &&
      <Modal<CrewSummaryRow>
        maxWidth={'sm'}
        open={open}
        title='Lost Time'
        data={{
          rowData: modalData,
          fields: LostTimeFields,
          columns: [
            {
              title: '',
              fieldKeys: [
                'noWorkAvailable',
                'wetWeatherHours',
                'breakdownOfPlantOrEquipment',
              ],
            },
            {
              title: '',
              fieldKeys: [
                'cancelledByCustomer',
                'cancelledByInternalIssue',
                'cancelledByCrewLeave',
                'trainingCrew',
              ],
            },
          ],
        }}
        onSuccess={handleSave}
        onClose={closeModal}
        onCancel={closeModal}
      />
    }
  </Wrapper>
}

const Wrapper = styled.div`

`

export default CompDRSLostTime